/**
 * Click IDs persisted in localStorage (via `useUtmParams`) and reinjected into
 * allowlisted signup `search_params` when the current URL has been stripped
 * (OAuth redirects, in-app navigation).
 *
 * Allowlisting a key in `get-allowlisted-search-params.ts` is not enough —
 * without persistence + this fallback, Google iOS (`gbraid`/`wbraid`) and
 * other click IDs drop to zero after the first navigation.
 *
 * Stored entries expire after `PERSISTED_PARAM_TTL_MS`, so a months-old click
 * cannot attribute a later signup.
 *
 * Warehouse contract: the top-level `utm_*` fields on the signup payload are
 * authoritative — storage keeps them populated whether or not the URL was
 * stripped. `search_params` mirrors the URL plus these click-ID fallbacks, so on
 * a stripped URL it carries click IDs and no `utm_*` keys — except `utm_term`, plus
 * `portfolio_entry` / `cta_location`, which have no top-level field and ride the
 * same fallback (`searchParamFallbacks` in `use-utm-params.ts`). Read it as raw
 * evidence, never as the source of truth for campaign attribution.
 */
export const PERSISTED_CLICK_ID_KEYS = ['ttclid', 'gclid', 'fbclid', 'msclkid', 'ttp', 'gbraid', 'wbraid'] as const;

export type PersistedClickIdKey = (typeof PERSISTED_CLICK_ID_KEYS)[number];

export const CLICK_ID_STORAGE_KEYS = {
  ttclid: '__ttclid',
  gclid: '__gclid',
  fbclid: '__fbclid',
  msclkid: '__msclkid',
  ttp: '__ttp',
  gbraid: '__gbraid',
  wbraid: '__wbraid',
} as const satisfies Record<PersistedClickIdKey, string>;

export interface PersistedClickIds {
  fbclid: string;
  gbraid: string;
  gclid: string;
  msclkid: string;
  ttclid: string;
  ttp: string;
  wbraid: string;
}

export function toClickIdFallbacks(ids: PersistedClickIds): Record<PersistedClickIdKey, string> {
  return { ...ids };
}
